import {Helmet} from '@dr.pogodin/react-helmet';
import {openConfirmDialog, openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeleteTireSpeedRatioMutation,
  usePostTireSpeedRatioSetActiveMutation,
  usePostTireSpeedRatioSetInactiveMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function TireInventorySpeedRatio() {
  const navigate = useNavigate();

  const [deleteTireSpeedRatio] = useDeleteTireSpeedRatioMutation();
  const [postTireSpeedRatioSetActive] = usePostTireSpeedRatioSetActiveMutation();
  const [postTireSpeedRatioSetInactive] = usePostTireSpeedRatioSetInactiveMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        navigate(composePath(settingsRoutes.tireSpeedRatioDetail, {params: {id}}))
      )
      .with('setAsInactive', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireSpeedRatioSetInactive({tireSpeedRatioId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setAsActive', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireSpeedRatioSetActive({tireSpeedRatioId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteTireSpeedRatio({tireSpeedRatioId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      );
  };

  return (
    <>
      <Helmet title={i18n.t('entity.tiresInventory.labels.tireSpeedRatio')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.tiresInventory.labels.tireSpeedRatio'),
          actions: [
            {
              type: 'button',
              title: i18n.t('entity.tiresInventory.labels.newTireSpeedRatio'),
              onClick: () => navigate(settingsRoutes.tireSpeedRatioCreate),
            },
          ],
        }}
        description={i18n.t('entity.tiresInventory.labels.tireSpeedRatioDescription')}
        data-testid={testIds.settings.tireSpeedRatio('template')}
      >
        <DmsDataGrid
          gridCode="tire-speed-ratio"
          actionCallback={actionCallback}
          data-testid={testIds.settings.tireSpeedRatio('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
